import { forwardRef, useEffect, useRef, useState } from 'react';
import { Input } from '../../../../../components/ui/input';
import { Switch } from '../../../../../components/ui/switch';
import type { TableExplorerCellValueCodec } from '../../../table-explorer/components/tableExplorerHostPorts';

export type CodecFilterValue = string | number | bigint | boolean | Date | null;

type Props = {
  value: CodecFilterValue | undefined;
  onChange: (value: CodecFilterValue) => void;
  valueCodec: TableExplorerCellValueCodec;
  valueTag?: string;
  /** Reports whether the current draft text is a committable value (`undefined`) or why not. */
  onValidityChange?: (error: string | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  'aria-label'?: string;
};

type Parsed = { ok: true; value: CodecFilterValue } | { ok: false; error: string };

const parseDraft = (codec: TableExplorerCellValueCodec, text: string, tag: string | undefined): Parsed => {
  const parsed = codec.parse(text, tag, { empty: text === '' });
  return parsed.ok ? { ok: true, value: parsed.value as CodecFilterValue } : { ok: false, error: parsed.error };
};

/**
 * A filter value input for a host value codec (Azure Table, S4 review D, D3). The text the user types is a DRAFT:
 * it is never re-derived from the parsed value while typing, so `1.` stays `1.` and the next `5` makes `1.5`, and a
 * lone `-` or an empty field is simply an invalid draft — the codec's parse error never escapes. Every valid draft
 * is reported through `onChange`; an invalid one is reported through `onValidityChange` (the chip refuses to commit
 * it) and shown as a visible error once the user tries to commit it (Enter, which is then swallowed, or leaving the
 * field). SQL engines never reach this component (`FilterValueInput` without a codec).
 */
export const CodecFilterValueInput = forwardRef<HTMLInputElement, Props>(function CodecFilterValueInput(
  { value, onChange, valueCodec, valueTag, onValidityChange, placeholder = 'Filter…', disabled = false, className = '', onKeyDown, onBlur, autoFocus = false, 'aria-label': ariaLabel },
  ref,
) {
  const format = (next: CodecFilterValue | undefined) => next === null || next === undefined ? '' : valueCodec.format(next, valueTag);
  const [text, setText] = useState(() => format(value));
  const [showError, setShowError] = useState(false);
  const emitted = useRef(value);
  // An outside change of the value (not one this input emitted) replaces the draft.
  useEffect(() => {
    if (Object.is(value, emitted.current)) return;
    emitted.current = value;
    setText(format(value));
    setShowError(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `format` is derived from the codec/tag props
  }, [value]);
  const parsed = parseDraft(valueCodec, text, valueTag);
  const error = parsed.ok ? undefined : parsed.error;
  useEffect(() => { onValidityChange?.(error); }, [error, onValidityChange]);
  useEffect(() => () => onValidityChange?.(undefined), [onValidityChange]);

  if (valueCodec.inputKind?.(valueTag) === 'boolean') {
    const checked = value === true || value === 'true';
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <Switch checked={checked} onCheckedChange={(next) => { emitted.current = next; onChange(next); }} disabled={disabled} aria-label={ariaLabel} />
        <span className="text-xs text-muted-foreground">{value === undefined || value === null ? 'Not set' : checked ? 'True' : 'False'}</span>
      </div>
    );
  }

  return (
    <span className="inline-flex flex-col">
      <Input
        ref={ref}
        type="text"
        autoComplete="off"
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        value={text}
        aria-invalid={error ? true : undefined}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          setShowError(false);
          const result = parseDraft(valueCodec, next, valueTag);
          if (result.ok) { emitted.current = result.value; onChange(result.value); }
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && error) {
            event.preventDefault();
            event.stopPropagation();
            setShowError(true);
            return;
          }
          onKeyDown?.(event);
        }}
        onBlur={() => { if (error) setShowError(true); onBlur?.(); }}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-label={ariaLabel}
        className={`h-7 text-xs ${error ? 'border-destructive' : ''} ${className}`}
      />
      {showError && error && <span role="alert" className="mt-0.5 max-w-56 text-[10px] leading-tight text-destructive">{error}</span>}
    </span>
  );
});
